import { Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { QueryKeys } from '../../api/config';
import { useAgisticsContext } from '../../app/agistics-context';
import { Load } from '../../types';
import { CarrierAssignment } from '../carriers/carrier-assignment';
import { DriverAssignment } from '../drivers/driver-assignment';
import { DrawerDialog } from '../shared/containers/drawer-dialog';
import { ModalDialog } from '../shared/containers/modal-dialog';
import { DraftsLoadsModal } from './drafts-loads';
import { BulkDrafts } from './drafts/bulk-drafts/bulk-drafts';
import { BulkDraftSubmissionFilter } from './drafts/bulk-drafts/bulk-drafts-types';
import { BulkModify } from './drafts/bulk-modify/bulk-modify';
import { BulkSchedule } from './drafts/bulk-schedule/bulk-schedule';
import { CascadeStopTimes } from './drafts/cascade/cascade-stop-times';
import { CreateDraft } from './drafts/create-draft/create-draft';
import { DeleteLoadsModal } from './drafts/delete-loads-modal';
import { AutoMergeDrafts } from './drafts/merge/auto-merge-drafts';
import { MergeDrafts } from './drafts/merge/merge-drafts';
import { PublishDraftsModal } from './drafts/publish-drafts-modal';
import { UnmergeDrafts } from './drafts/unmerge/unmerge-drafts';
import { EditCreateLoad } from './edit-create-load/edit-create-load';
import { DeliveryLog } from './loads/delivery-log/delivery-log';
import { ManuallyCompleteModal } from './loads/manually-complete/manually-complete-modal';
import { globalFilterRemover, showSelectedOnly } from './utils/drafts-loads-utils';
import { useDraftsLoadsContext } from './drafts-loads-context';

interface DraftLoadModalsProps {
    table: Table<Load>;
    isLoading: boolean;
    rowSelectionOrder: number[];

    dialogOpen: DraftsLoadsModal;
    setDialogOpen: (s: DraftsLoadsModal) => void;

    singleSelectedLoad: Load | null;
    setSingleSelectedLoad: (l: Load | null) => void;

}

export const DraftLoadModals: React.FC<DraftLoadModalsProps> = (props) => {
    const {
        table,
        isLoading,
        rowSelectionOrder,
        dialogOpen, setDialogOpen,
        singleSelectedLoad, setSingleSelectedLoad,
    } = props;

    const { updateDateFilters } = useAgisticsContext();
    const { getIsLoadStale } = useDraftsLoadsContext();

    function closeDialog() {
        setDialogOpen(null);
    }

    function unselectSingleSelectAndCloseDialog() {
        setSingleSelectedLoad(null);
        closeDialog();
    }

    function resetSelections() {
        table.resetRowSelection(true);
        globalFilterRemover(table, 'selectedFilter');
    }

    function resetSelectionsAndCloseDialog() {
        resetSelections();
        closeDialog();
    }

    function handleBulkCreationSubmission(submissionFilter?: BulkDraftSubmissionFilter) {
        if (submissionFilter?.createdIds && submissionFilter.createdIds.length > 0) {
            // Note: we discussed resetting all table filters (e.g. preferences.includedDates, globalFilter)
            // so that the newly created drafts are guaranteed to be shown,
            // but we decided for now to only address the date filter for the easiest implementation.
            // This requires users to manually reset all filters,
            // otherwise they might just see a blank table after creating drafts.

            // Set date filter to show only the newly created loads
            updateDateFilters(QueryKeys.drafts, {
                from: submissionFilter.weekRange[0],
                to: submissionFilter.weekRange[1],
                includeDrafts: true,
                mode: 'custom',
                offset: 0,
                page: '/drafts',
            });
            // Select the newly created loads and filter the table to show only them
            const newSelection: Record<string, boolean> = {};
            submissionFilter.createdIds.forEach(id => (newSelection[String(id)] = true));
            table.setRowSelection(() => newSelection);

            showSelectedOnly(table);
        }
        
        closeDialog();
    }

    // see notes in SelectionButtons for why this is here
    if (isLoading) {
        return <></>;
    }

    const multiSelectedLoads = table.getSelectedRowModel().rows.map(x => x.original);
    const filteredSelectedLoads = table.getFilteredSelectedRowModel().rows.map(x => x.original);
    const orderedSelectedLoads = rowSelectionOrder.map(x => {
        const load = multiSelectedLoads.find(selectedLoad => selectedLoad.id === x);
        return load;
    }).filter(x => !!x);

    const staleSafeDialogOpen = multiSelectedLoads.some(load => getIsLoadStale(load)) ? null : dialogOpen;

    return (
        <>
            <ModalDialog
                title={L.createDraft()}
                onClose={unselectSingleSelectAndCloseDialog}
                open={staleSafeDialogOpen === 'createDraft'}
            >
                <CreateDraft
                    onSubmit={unselectSingleSelectAndCloseDialog}
                />
            </ModalDialog>
            <ModalDialog
                title={L.createDrafts()}
                open={staleSafeDialogOpen === 'createDrafts'}
                onClose={closeDialog}
            >
                <BulkDrafts onSubmit={handleBulkCreationSubmission}/>
            </ModalDialog>
            <ModalDialog
                title={L.loadDetails()}
                onClose={unselectSingleSelectAndCloseDialog}
                open={staleSafeDialogOpen === 'editCreateLoad'}
            >
                <EditCreateLoad
                    inputLoad={singleSelectedLoad}
                    onSubmit={unselectSingleSelectAndCloseDialog}
                />
            </ModalDialog>
            <ModalDialog
                title={L.deliveryLog()}
                onClose={unselectSingleSelectAndCloseDialog}
                open={staleSafeDialogOpen === 'deliveryLog'}
            >
                {singleSelectedLoad && <DeliveryLog load={singleSelectedLoad} />}
            </ModalDialog>
            <ModalDialog
                title={L.manuallyCompleteConfirmationTitle(`${multiSelectedLoads.length > 1 ? L.loads() : L.load()}`)}
                open={staleSafeDialogOpen === 'completeLoads'}
                onClose={closeDialog}
            >
                <ManuallyCompleteModal
                    completableLoads={multiSelectedLoads}
                    onSubmit={resetSelectionsAndCloseDialog}
                />
            </ModalDialog>
            <DrawerDialog
                title={L.assignDriver()}
                open={staleSafeDialogOpen === 'assignLoads'}
                onClose={closeDialog}
                anchor='right'
            >
                <DriverAssignment
                    loads={multiSelectedLoads}
                    onAssign={resetSelectionsAndCloseDialog}
                />
            </DrawerDialog>
            <DrawerDialog
                title={L.assignCarrier()}
                open={staleSafeDialogOpen === 'assignCarrier'}
                onClose={closeDialog}
                anchor='right'
            >
                <CarrierAssignment
                    loads={multiSelectedLoads}
                    onAssign={resetSelectionsAndCloseDialog}
                />
            </DrawerDialog>
            <DeleteLoadsModal
                open={staleSafeDialogOpen === 'deleteRecords'}
                records={multiSelectedLoads}
                unselectFn={() => resetSelections()}
                onResultCallback={closeDialog}
            />
            <ModalDialog
                title={L.bulkSchedule()}
                onClose={closeDialog}
                open={staleSafeDialogOpen === 'bulkScheduleDrafts'}
                modalContentProps={{ display: 'flex', overflow: 'hidden' }}
            >
                <BulkSchedule
                    drafts={orderedSelectedLoads}
                    onSubmit={() => {
                        table.resetExpanded(true);
                        showSelectedOnly(table);
                        closeDialog();
                    }}
                />
            </ModalDialog>
            <ModalDialog
                title={L.bulkModify()}
                onClose={closeDialog}
                open={staleSafeDialogOpen === 'bulkModifyDrafts'}
            >
                <BulkModify
                    drafts={multiSelectedLoads}
                    onSubmit={closeDialog}
                />
            </ModalDialog>
            <ModalDialog
                title={L.mergeDrafts()}
                onClose={closeDialog}
                open={staleSafeDialogOpen === 'mergeDrafts'}
            >
                <MergeDrafts
                    drafts={orderedSelectedLoads}
                    onSubmit={resetSelectionsAndCloseDialog}
                />
            </ModalDialog>
            <ModalDialog
                title={L.autoMergeDrafts()}
                onClose={closeDialog}
                open={staleSafeDialogOpen === 'autoMergeDrafts'}
            >
                <AutoMergeDrafts
                    drafts={filteredSelectedLoads}
                    onSubmit={() => {
                        resetSelectionsAndCloseDialog();
                        table.resetExpanded(true);
                    }}
                />
            </ModalDialog>
            <ModalDialog
                title={L.cascadeStopTimes()}
                onClose={closeDialog}
                open={staleSafeDialogOpen === 'cascadeDrafts'}
            >
                <CascadeStopTimes
                    drafts={multiSelectedLoads}
                    onSubmit={closeDialog}
                />
            </ModalDialog>
            <PublishDraftsModal
                open={staleSafeDialogOpen === 'publishDrafts'}
                records={multiSelectedLoads}
                unselectFn={() => resetSelections()}
                onResultCallback={closeDialog}
            />
            <ModalDialog
                title={L.unmerge()}
                open={staleSafeDialogOpen === 'unmergeLoad'}
                onClose={unselectSingleSelectAndCloseDialog}
            >
                {singleSelectedLoad && <UnmergeDrafts
                    loadId={singleSelectedLoad.id}
                    mergedFromOrderIds={singleSelectedLoad.mergedFromOrderIds ? singleSelectedLoad.mergedFromOrderIds : []}
                    unmergeCloseDialog={unselectSingleSelectAndCloseDialog}
                />}
            </ModalDialog>
        </>
    );
};
